import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, MagnifyingGlass, ArrowUp, ArrowDown, X, Plus, FloppyDisk, Path as PathIcon, Clock, Lightning } from "@phosphor-icons/react";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip as LTooltip, useMap } from "react-leaflet";
import { TILE_URL, TILE_ATTR } from "@/lib/mapIcons";

export default function RouteDesigner() {
  const [routes, setRoutes] = useState([]);
  const [allStops, setAllStops] = useState([]);
  const [routeId, setRouteId] = useState("");
  const [seq, setSeq] = useState([]); // ordered list of stop objects
  const [q, setQ] = useState("");
  const [snap, setSnap] = useState({ geometry: [], distance_km: 0, duration_min: 0, cached: false });
  const [snapping, setSnapping] = useState(false);
  const debounceRef = useRef(null);

  useEffect(()=>{
    api.get("/routes").then(({data})=>setRoutes(data));
    api.get("/bus-stops/all").then(({data})=>setAllStops(data));
  }, []);

  useEffect(()=>{
    if (!routeId) { setSeq([]); return; }
    api.get(`/routes/${routeId}/stops`).then(({data})=>setSeq(data.stops || []));
  }, [routeId]);

  // Debounced road-snap whenever the sequence changes
  useEffect(()=>{
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const valid = seq.filter(s=>s.lat&&s.lng);
    if (valid.length < 2) { setSnap({ geometry: [], distance_km: 0, duration_min: 0, cached: false }); return; }
    let cancelled = false; // ignore out-of-order responses from superseded requests
    debounceRef.current = setTimeout(async () => {
      setSnapping(true);
      try {
        const { data } = await api.post("/routes/snap", {
          coords: valid.map(s => [s.lat, s.lng]),
        });
        if (!cancelled) setSnap(data);
      } catch (e) {
        if (!cancelled) setSnap({ geometry: [], distance_km: 0, duration_min: 0, cached: false, error: true });
      } finally { if (!cancelled) setSnapping(false); }
    }, 600);
    return () => { cancelled = true; if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [seq]);

  const filtered = useMemo(()=>{
    const s = q.toLowerCase();
    return allStops.filter(x =>
      (q==="" || x.bus_stop_name.toLowerCase().includes(s) || (x.bus_stop_code||"").toLowerCase().includes(s))
      && !seq.find(y => y.bus_stop_id === x.bus_stop_id)
    ).slice(0, 50);
  }, [allStops, q, seq]);

  const add = (s) => setSeq(p=>[...p, s]);
  const remove = (i) => setSeq(p=>p.filter((_,idx)=>idx!==i));
  const move = (i, dir) => setSeq(p=>{
    const a=[...p], j=i+dir; if(j<0||j>=a.length) return p;
    [a[i],a[j]]=[a[j],a[i]]; return a;
  });

  const save = async () => {
    if (!routeId) { toast.error("Select a route"); return; }
    try {
      await api.post(`/routes/${routeId}/stops`, { stop_ids: seq.map(s=>s.bus_stop_id) });
      toast.success(`Saved ${seq.length} stops on ${routeId}${snap.distance_km ? ` · ${snap.distance_km}km road` : ""}`);
    } catch { toast.error("Save failed"); }
  };

  // Memoised so FitToBounds only re-fits when the stops actually change, not on every render
  const stopPts = useMemo(()=>seq.filter(s=>s.lat&&s.lng).map(s=>[s.lat,s.lng]), [seq]);

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Network Planning</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Route Master Designer</h1>
        </div>
        <button onClick={save} disabled={!routeId} className="ktcl-btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-50 whitespace-nowrap">
          <FloppyDisk size={14}/><span className="hidden sm:inline">Save Route Sequence</span><span className="sm:hidden">Save</span>
        </button>
      </div>

      <div className="ktcl-card p-3 lg:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <span className="text-xs font-semibold text-slate-600 shrink-0">Route:</span>
        <select value={routeId} onChange={e=>setRouteId(e.target.value)} className="flex-1 max-w-md px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
          <option value="">— Choose route —</option>
          {routes.map(r=> <option key={r.id} value={r.route_id}>{`${r.route_id} · ${r.name}`}</option>)}
        </select>
        <div className="flex items-center gap-3 sm:ml-auto text-xs flex-wrap">
          <span className="flex items-center gap-1 text-slate-600">
            <MapPin size={12} className="text-[#1E3A8A]"/>{seq.length} stops
          </span>
          <span className="flex items-center gap-1 text-slate-600">
            <PathIcon size={12} className="text-emerald-600"/>
            {snapping ? <span className="italic text-slate-400">routing…</span> : (
              <>
                <span className="font-semibold tabular-nums">{snap.distance_km || 0} km</span>
                <span className="text-slate-400">road</span>
              </>
            )}
          </span>
          <span className="flex items-center gap-1 text-slate-600">
            <Clock size={12} className="text-amber-600"/>
            <span className="font-semibold tabular-nums">{snap.duration_min || 0} min</span>
          </span>
          {snap.cached && (
            <span className="flex items-center gap-0.5 text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded text-[10px] font-semibold">
              <Lightning size={10} weight="fill"/>cached
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Stops library */}
        <div className="ktcl-card overflow-hidden flex flex-col" style={{maxHeight: 600}}>
          <div className="p-3 border-b border-slate-200">
            <div className="label-cap text-slate-500 mb-2">Add Stops</div>
            <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-md">
              <MagnifyingGlass size={14}/>
              <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search stop…" className="bg-transparent text-sm outline-none flex-1"/>
            </div>
          </div>
          <div className="flex-1 overflow-auto scroll-thin">
            {filtered.map(s=>(
              <button key={s.id} onClick={()=>add(s)} disabled={!routeId} className="w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2">
                <Plus size={12} className="text-[#22C55E]"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{s.bus_stop_name}</div>
                  <div className="text-[10px] text-slate-500">#{s.bus_stop_id} · {s.bus_stop_code}</div>
                </div>
              </button>
            ))}
            {filtered.length===0 && <div className="p-6 text-xs text-slate-400 text-center italic">No matching stops</div>}
          </div>
        </div>

        {/* Sequence */}
        <div className="ktcl-card overflow-hidden flex flex-col" style={{maxHeight: 600}}>
          <div className="p-3 border-b border-slate-200">
            <div className="label-cap text-slate-500">Sequence</div>
            <div className="text-xs text-slate-500">{seq.length} stops · click ↑/↓ to reorder</div>
          </div>
          <div className="flex-1 overflow-auto scroll-thin">
            {seq.map((s,i)=>(
              <div key={s.id} className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 group">
                <div className="w-6 h-6 rounded-full bg-[#1E3A8A] text-white text-[10px] flex items-center justify-center font-bold shrink-0">{i+1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{s.bus_stop_name}</div>
                  <div className="text-[10px] text-slate-500">{s.bus_stop_code} · {s.lat?.toFixed(3)}, {s.lng?.toFixed(3)}</div>
                </div>
                <div className="flex gap-0.5 opacity-50 group-hover:opacity-100">
                  <button onClick={()=>move(i,-1)} className="p-1 hover:bg-slate-100 rounded"><ArrowUp size={11}/></button>
                  <button onClick={()=>move(i,1)} className="p-1 hover:bg-slate-100 rounded"><ArrowDown size={11}/></button>
                  <button onClick={()=>remove(i)} className="p-1 hover:bg-red-50 text-red-500 rounded"><X size={11}/></button>
                </div>
              </div>
            ))}
            {seq.length===0 && <div className="p-6 text-xs text-slate-400 text-center italic">Pick stops from the left to build the route</div>}
          </div>
        </div>

        {/* Map preview */}
        <div className="ktcl-card overflow-hidden flex flex-col" style={{height: 600}}>
          <div className="p-3 border-b border-slate-200 flex items-start justify-between gap-2">
            <div>
              <div className="label-cap text-slate-500">Route Preview</div>
              <div className="text-xs text-slate-500">{snap.geometry.length > 0 ? "Snapped to actual roads via OSRM" : "Pick ≥ 2 stops to see road geometry"}</div>
            </div>
            {snap.geometry.length > 0 && (
              <div className="flex items-center gap-1 text-[10px] flex-wrap justify-end">
                <Legend color="#1E3A8A" label="Road"/>
                <Legend color="#94A3B8" label="Straight" dashed/>
              </div>
            )}
          </div>
          <div className="flex-1 min-h-0 relative">
            <MapContainer center={[15.39, 73.99]} zoom={10} maxZoom={19}
              style={{position:"absolute", inset:0}}>
              {/* Esri's gray canvas only has tiles to z16; let Leaflet upscale beyond that instead of going blank */}
              <TileLayer url={TILE_URL} attribution={TILE_ATTR} maxNativeZoom={16} maxZoom={19}/>
              <AutoResize/>
              {/* Straight-line ghost (helpful to compare) */}
              {stopPts.length>1 && (
                <Polyline positions={stopPts} pathOptions={{color:"#94A3B8", weight:1.5, opacity:0.6, dashArray:"4 4"}}/>
              )}
              {/* Road-snapped polyline */}
              {snap.geometry.length > 1 && (
                <Polyline positions={snap.geometry} pathOptions={{color:"#1E3A8A", weight:4, opacity:0.85}}/>
              )}
              {/* Stop markers on top */}
              {seq.map((s,i)=>s.lat&&s.lng && (
                <CircleMarker key={s.id} center={[s.lat,s.lng]} radius={i===0||i===seq.length-1?8:5}
                  pathOptions={{color: i===0?"#22C55E":i===seq.length-1?"#EF4444":"#1E3A8A", fillColor: i===0?"#22C55E":i===seq.length-1?"#EF4444":"#1E3A8A", fillOpacity: 0.85}}>
                  <LTooltip>{i+1}. {s.bus_stop_name} ({s.bus_stop_code})</LTooltip>
                </CircleMarker>
              ))}
              <FitToBounds points={snap.geometry.length > 1 ? snap.geometry : stopPts}/>
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function FitToBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    try {
      if (points.length === 1) map.setView(points[0], 14);
      else map.fitBounds(points, { padding: [30, 30], maxZoom: 16 });
    } catch (e) { /* noop */ }
  }, [points, map]);
  return null;
}

// Leaflet doesn't notice container size changes (sidebar collapse, window resize, layout settling)
function AutoResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    map.invalidateSize();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

const Legend = ({ color, label, dashed }) => (
  <span className="inline-flex items-center gap-1 text-slate-600">
    <span className="inline-block w-4 h-0.5" style={{ background: dashed ? "transparent" : color, borderTop: dashed ? `1.5px dashed ${color}` : "none" }}/>
    {label}
  </span>
);
